import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { 
  FiTrendingUp, 
  FiHome, 
  FiUsers, 
  FiHeart, 
  FiAward, 
  FiGift, 
  FiStar,
  FiZap,
  FiCalendar,
  FiTruck,
  FiShield,
  FiPackage,
  FiChevronRight,
  FiPlay,
  FiCheckCircle,
  FiArrowRight,
  FiChevronLeft,
  FiChevronDown,
  FiDownload
} from 'react-icons/fi';
import { 
  GiTreeBranch, 
  GiReceiveMoney, 
  GiDiamondTrophy,
  GiMedicines,
  GiLotus
} from 'react-icons/gi';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero background images with Unsplash collection - Enhanced with better visibility
  const heroImages = [
    {
      url: "https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      title: "Success Together",
      overlay: "from-[#0F2F3D]/70 to-[#1a4b5e]/60",
      description: "Building wealth through collaboration"
    },
    {
      url: "https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      title: "Team Collaboration",
      overlay: "from-[#0F2F3D]/65 to-[#E4572E]/50",
      description: "Strong teams create lasting success"
    },
    {
      url: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      title: "Business Growth",
      overlay: "from-[#1a4b5e]/70 to-[#0F2F3D]/60",
      description: "Transform your financial future"
    },
    {
      url: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      title: "Community Success",
      overlay: "from-[#0F2F3D]/65 to-[#F47C4D]/55",
      description: "Join thousands of successful members"
    },
    {
      url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      title: "Financial Freedom",
      overlay: "from-[#0F2F3D]/70 to-[#2E7D32]/60",
      description: "Achieve your dreams with US"
    }
  ];

  useEffect(() => {
    fetchFeaturedProducts();
    
    // Auto-rotate hero images
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await productAPI.getAllProducts({ limit: 6 });
      setFeaturedProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const benefits = [
    {
      icon: <GiReceiveMoney className="w-8 h-8" />,
      title: 'Wealth Creation',
      description: 'Build your financial empire with proven strategies',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: <FiHeart className="w-8 h-8" />,
      title: 'Family First',
      description: 'Secure your loved ones with sustainable income',
      color: 'from-red-400 to-pink-500'
    },
    {
      icon: <FiHome className="w-8 h-8" />,
      title: 'Luxury Lifestyle',
      description: 'Live the life you deserve with unlimited potential',
      color: 'from-blue-400 to-indigo-500'
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: 'Community Growth',
      description: 'Build a thriving network of success-driven people',
      color: 'from-purple-400 to-purple-600'
    }
  ];

  const incomeStreams = [
    { 
      icon: <FiTrendingUp className="w-6 h-6" />, 
      title: 'Direct Sponsor Bonus', 
      value: '15%', 
      description: 'Instant rewards on every direct referral',
      gradient: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: <GiTreeBranch className="w-6 h-6" />, 
      title: 'Level Income', 
      value: '15 Levels', 
      description: 'Earn from 15 generations deep',
      gradient: 'from-green-500 to-emerald-500'
    },
    { 
      icon: <FiGift className="w-6 h-6" />, 
      title: 'Monthly Rewards', 
      value: '₹6000', 
      description: 'Monthly product incentives',
      gradient: 'from-purple-500 to-pink-500'
    },
    { 
      icon: <FiZap className="w-6 h-6" />, 
      title: 'Daily Boosting', 
      value: '10%', 
      description: 'Daily performance bonuses',
      gradient: 'from-yellow-500 to-orange-500'
    },
    { 
      icon: <FiStar className="w-6 h-6" />, 
      title: 'Real-Time Bonanza', 
      value: '₹1 Lac', 
      description: 'Exponential growth rewards',
      gradient: 'from-indigo-500 to-blue-500'
    },
    { 
      icon: <GiDiamondTrophy className="w-6 h-6" />, 
      title: 'Lifetime Rewards', 
      value: '₹50 Cr', 
      description: 'Rank-based lifetime achievements',
      gradient: 'from-amber-500 to-orange-500'
    },
    { 
      icon: <FiAward className="w-6 h-6" />, 
      title: 'Royalty Club', 
      value: 'Fortuner', 
      description: 'Luxury car rewards for elite performers',
      gradient: 'from-red-500 to-rose-500'
    }
  ];

  const ranks = [
    { name: 'STAR', business: '1 Lac', reward: '₹5,000', icon: '⭐', color: 'from-yellow-400 to-yellow-500' },
    { name: 'SILVER STAR', business: '2.5 Lacs', reward: '₹12,500', icon: '🌟', color: 'from-gray-300 to-gray-400' },
    { name: 'GOLD', business: '5 Lacs', reward: '₹25,000', icon: '💫', color: 'from-yellow-500 to-yellow-600' },
    { name: 'PLATINUM', business: '10 Lacs', reward: '₹50,000', icon: '💎', color: 'from-cyan-400 to-cyan-500' },
    { name: 'DIAMOND', business: '25 Lacs', reward: '₹1.25 Lacs', icon: '🔷', color: 'from-blue-400 to-blue-500' },
    { name: 'BLUE DIAMOND', business: '50 Lacs', reward: '₹2.5 Lacs', icon: '💠', color: 'from-indigo-400 to-indigo-500' }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Diamond Rank Achiever',
      image: 'https://randomuser.me/api/portraits/men/1.jpg',
      quote: 'Joined 2 years ago, now earning ₹2.5 Lakhs monthly. This platform changed my life!',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      role: 'Platinum Rank Achiever',
      image: 'https://randomuser.me/api/portraits/women/1.jpg',
      quote: 'The best decision I made for my family. Financial freedom is now a reality.',
      rating: 5
    },
    {
      name: 'Amit Patel',
      role: 'Gold Rank Achiever',
      image: 'https://randomuser.me/api/portraits/men/2.jpg',
      quote: 'Amazing products and even better earning potential. Truly life-changing!',
      rating: 5
    }
  ];

  const stats = [
    { value: '50K+', label: 'Happy Members', icon: '👥' },
    { value: '100+', label: 'Ayurvedic Products', icon: '🌿' },
    { value: '₹10Cr+', label: 'Income Generated', icon: '💰' },
    { value: '4.8/5', label: 'Member Satisfaction', icon: '⭐' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0F2F3D] to-[#1a4b5e]">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-[#E4572E] border-t-transparent"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-10 w-10 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2F2F2] to-white overflow-hidden">
      {/* Hero Section with Animated Background Carousel */}
      <section className="relative h-screen min-h-[800px] text-white overflow-hidden">
        {/* Background Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-r ${image.overlay}`}></div>
            </div>
          ))}
        </div>

        {/* Animated Particles/Lotus Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-32 h-32 opacity-10 animate-float">
            <GiLotus className="w-full h-full text-white" />
          </div>
          <div className="absolute bottom-20 right-10 w-40 h-40 opacity-10 animate-float animation-delay-2000">
            <GiLotus className="w-full h-full text-white" />
          </div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 opacity-10 animate-float animation-delay-4000">
            <GiLotus className="w-full h-full text-white" />
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-lg hover:bg-white/20 text-white rounded-full p-3 transition-all duration-300 group"
        >
          <FiChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-lg hover:bg-white/20 text-white rounded-full p-3 transition-all duration-300 group"
        >
          <FiChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-300 ${
                index === currentSlide
                  ? 'w-12 h-2 bg-[#E4572E] rounded-full'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/80 rounded-full'
              }`}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
            <div className="space-y-8 animate-fadeInUp">
              {/* Animated Badge */}

              {/* Main Headline with Typing Animation Effect */}
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="block text-white">KARLO SAPNE</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#F47C4D] to-[#E4572E] relative">
                  MUTTHI ME
                  <span className="absolute -bottom-2 left-0 w-20 h-1 bg-gradient-to-r from-[#F47C4D] to-transparent"></span>
                </span>
              </h1>
              
              <h2 className="text-3xl lg:text-4xl font-bold text-[#F47C4D] animate-pulse">
                GO WEALTH. BEST LIFE.
              </h2>
              
              <p className="text-xl text-gray-200 leading-relaxed max-w-lg backdrop-blur-sm bg-black/10 p-4 rounded-2xl">
                Transform your dreams into reality with India's fastest-growing opportunity. Wealth, family happiness, and success - all in one place.
              </p>
              
              {/* Enhanced CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="/desimart.apk"
                  download="DesiMart.apk"
                  className="group relative px-8 py-4 bg-gradient-to-r from-[#0F2F3D] to-[#1a4b5e] text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/40 transition-all duration-300 font-bold text-lg overflow-hidden transform hover:scale-105"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <FiDownload className="mr-2" />
                    Download App
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1a4b5e] to-[#0F2F3D] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                <a
                  href="/desimart-plan.pdf"
                  download="DesiMart-Plan.pdf"
                  className="group relative px-8 py-4 bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white rounded-xl hover:shadow-2xl hover:shadow-purple-500/40 transition-all duration-300 font-bold text-lg overflow-hidden transform hover:scale-105"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <FiDownload className="mr-2" />
                    Download Plan
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                {!isAuthenticated ? (
                  <>
                    <Link
                      to="/register"
                      className="group relative px-8 py-4 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-xl hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 font-bold text-lg overflow-hidden transform hover:scale-105"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Get Started Now
                        <FiArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-[#F47C4D] to-[#E4572E] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </Link>
                    <Link
                      to="/plans"
                      className="group px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-xl hover:bg-white/10 hover:border-white hover:scale-105 transition-all duration-300 font-bold text-lg backdrop-blur-sm flex items-center justify-center"
                    >
                      View Plans
                      <FiChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/dashboard"
                    className="group px-8 py-4 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-xl hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 font-bold text-lg transform hover:scale-105"
                  >
                    Go to Dashboard
                    <FiChevronRight className="inline ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </div>

              {/* Enhanced Trust Badges */}
              <div className="flex flex-wrap items-center gap-6 pt-6">
                <div className="flex items-center group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-lg rounded-full flex items-center justify-center mr-3 group-hover:bg-[#F47C4D]/20 transition-all">
                    <FiShield className="w-5 h-5 text-[#F47C4D]" />
                  </div>
                  <span className="text-sm">100% Ayurvedic</span>
                </div>
                <div className="flex items-center group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-lg rounded-full flex items-center justify-center mr-3 group-hover:bg-[#F47C4D]/20 transition-all">
                    <FiTruck className="w-5 h-5 text-[#F47C4D]" />
                  </div>
                  <span className="text-sm">Pan India Delivery</span>
                </div>
                <div className="flex items-center group">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-lg rounded-full flex items-center justify-center mr-3 group-hover:bg-[#F47C4D]/20 transition-all">
                    <FiPackage className="w-5 h-5 text-[#F47C4D]" />
                  </div>
                  <span className="text-sm">Quality Assured</span>
                </div>
              </div>
            </div>

            {/* Enhanced Hero Visual with 3D Effect */}
            <div className="hidden lg:block animate-fadeInRight">
              <div className="relative transform hover:scale-105 transition-transform duration-500">
                {/* Floating Elements with Animation */}
                <div className="absolute -top-12 -left-12 w-32 h-32 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/30 flex items-center justify-center animate-float shadow-2xl">
                  <div className="relative">
                    <GiMedicines className="w-16 h-16 text-[#F47C4D]" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
                  </div>
                </div>
                
                <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/30 flex items-center justify-center animate-float animation-delay-2000 shadow-2xl">
                  <GiDiamondTrophy className="w-20 h-20 text-yellow-400" />
                </div>

                <div className="absolute top-20 -right-16 w-28 h-28 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/30 flex items-center justify-center animate-float animation-delay-4000 shadow-2xl">
                  <FiTrendingUp className="w-14 h-14 text-green-400" />
                </div>

                {/* Main Card with Glass Morphism */}
                {/* <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="grid grid-cols-2 gap-6">
                    {benefits.map((benefit, index) => (
                      <div 
                        key={index} 
                        className="text-center group hover:transform hover:scale-110 transition-all duration-300 cursor-pointer"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className={`inline-flex p-4 bg-gradient-to-br ${benefit.color} rounded-2xl text-white mb-3 group-hover:shadow-2xl group-hover:shadow-${benefit.color.split(' ')[1]}/30 transform group-hover:-translate-y-1 transition-all`}>
                          {benefit.icon}
                        </div>
                        <h3 className="font-bold text-sm text-white">{benefit.title}</h3>
                        <p className="text-xs text-white/70 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {benefit.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <div className="flex flex-col items-center text-white/80 hover:text-white transition-colors cursor-pointer">
            <span className="text-sm mb-2">Scroll to explore</span>
            <FiChevronDown className="w-5 h-5" />
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F2F2F2"/>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F2F2F2]">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-4xl mb-2 transform group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-[#0F2F3D] group-hover:text-[#E4572E] transition-colors">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Income Streams Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-[#E4572E] font-semibold text-sm uppercase tracking-wider">Earning Potential</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2F3D] mt-2 mb-4">
              7 Powerful Income Streams
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to earn and grow your wealth with our innovative compensation plan
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {incomeStreams.map((stream, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stream.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
                
                <div className={`inline-flex p-3 bg-gradient-to-br ${stream.gradient} text-white rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {stream.icon}
                </div>
                
                <h3 className="font-bold text-[#0F2F3D] text-lg mb-1 group-hover:text-[#E4572E] transition-colors">
                  {stream.title}
                </h3>
                
                <div className="text-2xl font-bold text-[#E4572E] mb-2">
                  {stream.value}
                </div>
                
                <p className="text-gray-600 text-sm">
                  {stream.description}
                </p>

                {/* Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stream.gradient} opacity-0 group-hover:opacity-10 blur-xl rounded-2xl transition-opacity duration-500`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rank Progression with Visual Timeline */}
      <section className="py-20 bg-gradient-to-b from-[#F2F2F2] to-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-[#E4572E] font-semibold text-sm uppercase tracking-wider">Career Path</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2F3D] mt-2 mb-4">
              Climb the Success Ladder
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Clear path to growth with increasing rewards and recognition
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#E4572E] via-[#F47C4D] to-[#E4572E] transform -translate-y-1/2 hidden lg:block"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ranks.map((rank, index) => (
                <div
                  key={index}
                  className="group relative bg-white rounded-xl p-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
                >
                  {/* Rank Icon */}
                  <div className={`absolute -top-4 left-6 w-12 h-12 rounded-xl bg-gradient-to-br ${rank.color} flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {rank.icon}
                  </div>

                  <div className="mt-8">
                    <h3 className="font-bold text-xl text-[#0F2F3D] mb-3 group-hover:text-[#E4572E] transition-colors">
                      {rank.name}
                    </h3>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Business Volume</span>
                        <span className="font-semibold text-[#0F2F3D]">{rank.business}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Reward</span>
                        <span className="font-bold text-[#E4572E]">{rank.reward}</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${rank.color} rounded-full transform origin-left transition-transform duration-1000 group-hover:scale-x-100`}
                        style={{ width: `${(index + 1) * 16.66}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/ranks"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#0F2F3D] to-[#1a4b5e] text-white rounded-xl hover:shadow-lg hover:shadow-[#0F2F3D]/25 transition-all duration-300 font-semibold group"
            >
              View Complete Rank Structure
              <FiChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products with Modern Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-[#E4572E] font-semibold text-sm uppercase tracking-wider">Premium Collection</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2F3D] mt-2 mb-4">
              100% Ayurvedic Products
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nature's finest ingredients for your wellness journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.slice(0, 6).map((product, index) => (
              <div
                key={product._id}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
              >
                {/* Product Badge */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Premium
                </div>

                {/* Product Icon */}
                <div className="w-20 h-20 bg-gradient-to-br from-[#E4572E]/10 to-[#F47C4D]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <GiMedicines className="w-10 h-10 text-[#E4572E]" />
                </div>

                <h3 className="font-bold text-xl text-[#0F2F3D] mb-2 group-hover:text-[#E4572E] transition-colors">
                  {product.name}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {product.description}
                </p>

                <div className="flex items-center justify-between mt-6">
                  <div>
                    <span className="text-xs text-gray-500 line-through">₹{Math.round(product.price * 1.2)}</span>
                    <div className="text-2xl font-bold text-[#E4572E]">₹{product.price}</div>
                  </div>
                  
                  <Link
                    to="/products"
                    className="px-6 py-3 bg-gradient-to-r from-[#0F2F3D] to-[#1a4b5e] text-white rounded-xl hover:shadow-lg hover:shadow-[#0F2F3D]/25 transition-all duration-300 text-sm font-semibold"
                  >
                    View Details
                  </Link>
                </div>

                {/* Hover Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#E4572E] to-[#F47C4D] opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500"></div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-xl hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 font-semibold group"
            >
              Explore All Products
              <FiChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-[#F2F2F2] to-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-[#E4572E] font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2F3D] mt-2 mb-4">
              What Our Members Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real stories from real people who transformed their lives
            </p>
          </div>

          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                >
                  {/* Quote Icon */}
                  <div className="text-6xl text-[#E4572E]/20 mb-4">"</div>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {testimonial.quote}
                  </p>

                  <div className="flex items-center">
                    <div className="w-16 h-16 rounded-full overflow-hidden mr-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F2F3D]">{testimonial.name}</h4>
                      <p className="text-sm text-[#E4572E]">{testimonial.role}</p>
                      <div className="flex text-yellow-400 mt-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FiStar key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="relative rounded-3xl overflow-hidden group cursor-pointer">
            <img
              src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
              alt="Success Story"
              className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2F3D]/80 to-transparent flex items-center justify-center">
              <div className="text-center text-white">
                <div className="w-20 h-20 bg-[#E4572E] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 cursor-pointer hover:bg-[#F47C4D]">
                  <FiPlay className="w-8 h-8 ml-1" />
                </div>
                <h3 className="text-3xl font-bold mb-2">Watch Success Stories</h3>
                <p className="text-lg opacity-90">See how our members transformed their lives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background with Parallax Effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2F3D] to-[#1a4b5e]"></div>
          <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-[#E4572E]/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-[#F47C4D]/20 to-transparent"></div>
        </div>

        {/* Animated Circles */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#E4572E] rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-[#F47C4D] rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>

        <div className="max-w-4xl mx-auto px-5 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Write Your Success Story?
          </h2>
          
          <p className="text-xl text-gray-200 mb-10 leading-relaxed">
            Join thousands of successful entrepreneurs who are already living their dreams. 
            Start your journey today with just a few clicks.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <Link
                  to="/register"
                  className="group relative px-10 py-5 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-xl hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 font-bold text-lg overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    Join Now - It's Free
                    <FiChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F47C4D] to-[#E4572E] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
                <Link
                  to="/contact"
                  className="px-10 py-5 bg-transparent border-2 border-white/30 text-white rounded-xl hover:bg-white/10 hover:border-white transition-all duration-300 font-bold text-lg backdrop-blur-sm"
                >
                  Talk to an Expert
                </Link>
              </>
            ) : (
              <Link
                to="/dashboard"
                className="group px-10 py-5 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-xl hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 font-bold text-lg"
              >
                Go to Dashboard
                <FiChevronRight className="inline ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
            <div className="flex items-center text-white/80">
              <FiCheckCircle className="w-5 h-5 text-[#F47C4D] mr-2" />
              <span>No Hidden Fees</span>
            </div>
            <div className="flex items-center text-white/80">
              <FiCheckCircle className="w-5 h-5 text-[#F47C4D] mr-2" />
              <span>24/7 Support</span>
            </div>
            <div className="flex items-center text-white/80">
              <FiCheckCircle className="w-5 h-5 text-[#F47C4D] mr-2" />
              <span>Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* Add these animation styles to your global CSS or create a style tag */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 1s ease-out;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 1s ease-out;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Home;